import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddToListsModal from "./AddToListsModal";
import {
  buildOfflineEpubBlob,
  fetchOfflineReadyBookMetadata
} from "../utils/appendedChapterEpub";
import {
  cacheOfflineBook,
  isOfflineBookAvailable,
  listOfflineBooks,
  removeOfflineBook
} from "../utils/offlineLibrary";
import "./RecentsRow.css";
import "./LibraryPage.css";

function LibraryPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [books, setBooks] = useState([]);
  const [offlineBooks, setOfflineBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [offlineActionId, setOfflineActionId] = useState(null);
  const [listModalBook, setListModalBook] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const currentUserId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
  const rememberToken = localStorage.getItem("rememberToken") || "";

  const loadOfflineBooks = useCallback(() => {
    setOfflineBooks(listOfflineBooks(currentUserId));
  }, [currentUserId]);

  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const fetchLibrary = useCallback(async () => {
    if (!apiUrl || !currentUserId) {
      setBooks([]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/get-library-books.php?uid=${currentUserId}`,
        {
          credentials: "include",
          cache: "no-store"
        }
      );
      const data = await response.json();
      if (data.success) {
        setBooks(data.books || []);
      }
    } catch (error) {
      console.error("Failed to fetch library books:", error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, currentUserId]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  useEffect(() => {
    loadOfflineBooks();
    const refresh = () => {
      fetchLibrary();
      loadOfflineBooks();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("reader-closed", refresh);
    window.addEventListener("reader-progress-saved", refresh);
    window.addEventListener("offline-library-updated", loadOfflineBooks);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("reader-closed", refresh);
      window.removeEventListener("reader-progress-saved", refresh);
      window.removeEventListener("offline-library-updated", loadOfflineBooks);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchLibrary, loadOfflineBooks]);

  const allBooks = [
    ...books,
    ...offlineBooks
      .filter((offlineBook) => !books.some((book) => Number(book.id) === Number(offlineBook.id)))
      .map((offlineBook) => ({
        ...offlineBook,
        isOfflineOnly: true
      }))
  ];
  const visibleBooks = activeTab === "offline" ? offlineBooks : allBooks;
  const selectedBook = visibleBooks.find((book) => book.id === openMenuId)
    || books.find((book) => book.id === openMenuId)
    || offlineBooks.find((book) => book.id === openMenuId);
  const selectedBookIsOffline = selectedBook
    ? isOfflineBookAvailable(currentUserId, selectedBook.id)
    : false;

  const formatProgressLabel = (progress) => {
    const numeric = Number(progress || 0);
    if (numeric <= 0) return "1%";
    return `${Math.round(numeric)}%`;
  };

  const getProgressWidth = (progress) => {
    const numeric = Number(progress || 0);
    if (numeric <= 0) return "1%";
    return `${Math.min(100, numeric)}%`;
  };

  const handleMenuToggle = (event, epubId) => {
    event.stopPropagation();
    setOpenMenuId((current) => (current === epubId ? null : epubId));
  };

  const handleRemove = async (epubId) => {
    const targetBook = allBooks.find((book) => Number(book.id) === Number(epubId));
    if (targetBook?.isOfflineOnly) {
      try {
        if (isOfflineBookAvailable(currentUserId, epubId)) {
          await removeOfflineBook(currentUserId, epubId);
        }
        loadOfflineBooks();
        setOpenMenuId(null);
      } catch (error) {
        console.error("Failed to remove offline-only library item:", error);
      }
      return;
    }

    try {
      const form = new FormData();
      form.append("epub_id", epubId);
      form.append("uid", currentUserId);
      const response = await fetch(`${apiUrl}/remove-recent.php`, {
        method: "POST",
        credentials: "include",
        headers: rememberToken ? { "X-Remember-Token": rememberToken } : undefined,
        body: form
      });
      const data = await response.json();
      if (data.success) {
        setBooks((current) => current.filter((book) => book.id !== epubId));
        if (isOfflineBookAvailable(currentUserId, epubId)) {
          await removeOfflineBook(currentUserId, epubId);
        }
        loadOfflineBooks();
        setOpenMenuId(null);
      }
    } catch (error) {
      console.error("Failed to remove library item:", error);
    }
  };

  const handleOfflineToggle = async (book) => {
    if (!book || !currentUserId) return;

    setOfflineActionId(book.id);
    try {
      if (isOfflineBookAvailable(currentUserId, book.id)) {
        await removeOfflineBook(currentUserId, book.id);
      } else {
        const offlineMetadata = await fetchOfflineReadyBookMetadata({
          apiUrl,
          epubId: book.id,
          userId: currentUserId,
          rememberToken,
          fallback: book
        });
        const blob = await buildOfflineEpubBlob({
          apiUrl,
          epubId: book.id,
          rememberToken
        });
        await cacheOfflineBook({
          userId: currentUserId,
          epubId: book.id,
          metadata: offlineMetadata || book,
          blob
        });
      }
      loadOfflineBooks();
      setOpenMenuId(null);
    } catch (error) {
      console.error("Failed to update offline book:", error);
    } finally {
      setOfflineActionId(null);
    }
  };

  return (
    <div className="library-page app-shell-page">
      {listModalBook && (
        <AddToListsModal
          book={listModalBook}
          onClose={() => setListModalBook(null)}
          onSaved={() => {
            fetchLibrary();
          }}
        />
      )}

      <div className="library-page__inner">
        <div className="library-page__topbar">
          <h2 className="library-page__title mb-0">Your Library</h2>
        </div>

        <div className="library-page__switcher btn-neutral">
          <button
            type="button"
            className={`library-page__switch${activeTab === "all" ? " library-page__switch--active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            {`All Books • ${allBooks.length}`}
          </button>
          <button
            type="button"
            className={`library-page__switch${activeTab === "offline" ? " library-page__switch--active" : ""}`}
            onClick={() => setActiveTab("offline")}
          >
            {`Offline • ${offlineBooks.length}`}
          </button>
        </div>

        {loading ? (
          <div className="library-page__empty">Loading your library...</div>
        ) : visibleBooks.length === 0 ? (
          <div className="library-page__empty">
            {activeTab === "offline"
              ? "Books you make available offline will show up here."
              : "Books you interact with will show up here."}
          </div>
        ) : (
          <div className="library-grid">
            {visibleBooks.map((book) => (
              <div key={book.id} className="recent-card library-card">
                <div
                  className="recent-cover d-flex align-items-center justify-content-center"
                  onClick={() => navigate(`/epub/${book.id}/read`)}
                >
                  <span className="recent-cover-letter">
                    {book.title?.charAt(0).toUpperCase()}
                  </span>
                </div>

                <div className="recent-meta">
                  <span className="recent-percent">
                    {formatProgressLabel(book.progress_percent)}
                  </span>
                  <div className="recent-menu">
                    <span
                      className="material-symbols-rounded recent-menu-btn"
                      onClick={(event) => handleMenuToggle(event, book.id)}
                      aria-label={`Open menu for ${book.title}`}
                    >
                      more_horiz
                    </span>
                  </div>
                </div>

                <div className="recent-progress-track mt-2 mb-3">
                  <div
                    className="recent-progress-fill"
                    style={{ width: getProgressWidth(book.progress_percent) }}
                  />
                </div>

                <h5
                  className="recent-title"
                  onClick={() => navigate(`/epub/${book.id}/read`)}
                >
                  {book.title}
                </h5>
                <p className="recent-author">{book.author}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {openMenuId && (
        <div
          className="recent-menu-overlay"
          onClick={() => setOpenMenuId(null)}
        >
          <div
            className="recent-menu-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="recent-menu-item"
              onClick={() => {
                if (!selectedBook) return;
                setListModalBook(selectedBook);
                setOpenMenuId(null);
              }}
            >
              Add to Lists
            </button>
            <button
              className="recent-menu-item"
              onClick={() => {
                navigate(`/epub/${openMenuId}`);
                setOpenMenuId(null);
              }}
            >
              View Details
            </button>
            <button
              className="recent-menu-item"
              onClick={() => handleOfflineToggle(selectedBook)}
              disabled={!selectedBook || offlineActionId === selectedBook.id}
            >
              {offlineActionId === selectedBook?.id
                ? "Working..."
                : selectedBookIsOffline
                  ? "Remove Offline"
                  : "Make Offline"}
            </button>
            <button
              className="recent-menu-item recent-menu-item--danger"
              onClick={() => handleRemove(openMenuId)}
            >
              Delete From Library
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LibraryPage;
